import React from 'react';
import { NextPageContext } from 'next';
import { CmsCountry, CmsMemory } from '../integrations/contentful/types';
import { fetchCountry } from '../integrations/contentful/queries';
import H from '../components/elements/H';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import Container from '../components/grid/Container';
import { navbarLayout } from '../layouts/navbarLayout';
import BackButton from '../components/navigation/BackButton';
import { baseXUnit, breakpoints } from '../styles/theme';
import CmsTextareaContent from '../components/cms/CmsTextareaContent';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import HorizontalLine from '../components/common/HorizontalLine';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import { renderMemoryCardSafely } from '../components/memories/MemoryCard';
import Breadcrumbs from '../components/head/Breadcrumbs';
import DisplayOn from '../components/common/DisplayOn';
import CmsMediaFixedHeight from '../components/cms/CmsMediaFixedHeight';
import {
  DesktopHorizontalList,
  getDesktopHorizontalListTopControls
} from '../components/common/DesktopHorizontalList';
import DivButton from '../components/a11y/DivButton';
import MediaGalleryModal from '../components/media-gallery/MediaGalleryModal';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { TimPage } from './TimPage';

interface CountryPageProps {
  country?: CmsCountry;
}

interface MediaGalleryState {
  show: boolean;
}

const ANALYTICS_CATEGORY = 'Kaupunkisivu';
const imageHeightBase = [41, 41, 77];
const imageWidthBase = 77;

const CountryPage: TimPage<CountryPageProps> = ({ country }) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false
    });

  if (!country) {
    return null;
  }

  const relatedMemories = (country.fields.trips as CmsMemory[]) || [];
  const canonicalPath = generatePath(Paths.Country, {
    slug: country.fields.slug
  });

  return (
    <div data-cy="country-page">
      <SEO
        title={
          (country.fields.seoTitle || country.fields.title) + ' - Kaupunki'
        }
        canonicalPath={canonicalPath}
        description={
          country.fields.seoDescription || country.fields.leadParagraph
        }
        mainImage={country.fields.mainImage}
      />
      <Breadcrumbs
        items={[
          {
            name: t('Kaupungit'), // Or 'Matkakohteet' if we nest it there
            path: Paths.DestinationList
          },
          {
            name: country.fields.seoTitle || country.fields.title,
            path: canonicalPath
          }
        ]}
      />
      <Container>
        <Row>
          <Col size={24}>
            <Spacing bottom={[4, 4, 7]} top={[5, 5, 8]}>
              <Spacing bottom={1}>
                <H styling="h5">{t('Kaupunki')}</H>
              </Spacing>
              <H styling="h1">
                <strong>{country.fields.title}</strong>
              </H>
            </Spacing>
          </Col>
        </Row>
        <Row>
          <Col size={[24, 24, 11]}>
            <Spacing bottom={6}>
              {country.fields.mainImage && (
                <DivButton
                  data-cy="country-page-main-media"
                  onClick={() => {
                    setMediaGalleryState({ show: true });
                    trackAnalyticsEvent(
                      ANALYTICS_CATEGORY,
                      'Avattu mediagalleriamodaali',
                      country.fields.mainImage &&
                        country.fields.mainImage.fields.title
                    );
                  }}
                >
                  <CmsMediaFixedHeight
                    media={country.fields.mainImage}
                    fixedHeight={imageHeightBase}
                    maxWidth={imageWidthBase}
                    borderRadius={1}
                    hover
                  />
                </DivButton>
              )}
            </Spacing>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 12]}>
            <div className="ingress">
              <H styling="h3" tag="div">
                {country.fields.leadParagraph}
              </H>
              <Spacing top={[3, 3, 4]} bottom={9}>
                <div className="content">
                  <CmsTextareaContent content={country.fields.description} />
                </div>
              </Spacing>
            </div>
          </Col>
        </Row>
      </Container>
      {relatedMemories.length > 0 && <HorizontalLine expand={false} />}
      {relatedMemories.length > 0 && (
        <div data-testid="related-memories">
          <Container>
            <Row>
              <Col size={16}>
                <Spacing top={[8, 8, 10]} bottom={[6, 6, -3]}>
                  <H styling={'h2'}>
                    <strong>{t('Matkat')}</strong>
                  </H>
                  <H styling="h5" tag="div" color="gray" data>
                    {t('Kaupungissa')} {country.fields.title}
                  </H>
                </Spacing>
              </Col>
            </Row>
            <DisplayOn small>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.One} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn medium>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.Two} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn large>
              <DesktopHorizontalList
                elementsPerSlide={3}
                controls={getDesktopHorizontalListTopControls()}
              >
                {relatedMemories.map((memory) => {
                  return (
                    <Spacing key={memory.sys.id} bottom={4}>
                      {renderMemoryCardSafely(memory)}
                    </Spacing>
                  );
                })}
              </DesktopHorizontalList>
            </DisplayOn>
          </Container>
        </div>
      )}
      <Spacing bottom={14} />
      {country.fields.mainImage && (
        <MediaGalleryModal
          items={[
            {
              media: country.fields.mainImage
            }
          ]}
          closeDialog={() =>
            setMediaGalleryState({ ...mediaGalleryState, show: false })
          }
          showDialog={mediaGalleryState.show}
        />
      )}
      {/*language=CSS*/}
      <style jsx>{`
        @media ${breakpoints.medium} {
          .ingress {
            max-width: ${baseXUnit(95)};
          }
        }
      `}</style>
    </div>
  );
};

CountryPage.getLayout = (page) =>
  navbarLayout(
    <BackButton
      defaultTitle={t('Matkakohteet')}
      defaultPath={Paths.DestinationList}
    />,
    page
  );

CountryPage.getInitialProps = async function ({ query }: NextPageContext) {
  const countrySlug = String(query.slug);
  const country = await fetchCountry(countrySlug);
  const httpErrorCode = !country ? 404 : undefined;
  return {
    country,
    httpErrorCode
  };
};

export default CountryPage;
